/**
 * Routeur et barre d'onglets (ARCHITECTURE.md §3, §6.1).
 *
 * Une route de premier niveau par onglet (specs §8.2). La structure a été
 * arrêtée dès la V1 pour absorber le 5ᵉ onglet de la V2 **sans
 * réorganisation** : elle l'a fait, et à l'étape 5 de la V2 la promesse est
 * tenue au mot près — une route de plus, une entrée de plus dans `TABS`, un
 * membre de plus à `TabRoot`. Rien d'autre n'a bougé.
 *
 * ⚠️ L'onglet s'appelle **« Musculations »** *(28/08/2026)*. Le §2.5 avait
 * mesuré « Entraînement » comme intenable et choisi « Séances » ; l'onglet
 * ayant récupéré le référentiel d'exercices, ce dernier nom ne le décrivait
 * plus. Cf. `TabBar.tsx` pour ce que ce libellé plus long coûte.
 *
 * État à l'étape 10 de la V1 — les quatre premiers onglets sont complets :
 *   - **Journal** — bandeau de restant, repas, quatre voies d'ajout, navigation
 *     entre les jours.
 *   - **Bibliothèque** — trois sections en sous-routes : modèles, aliments,
 *     recettes.
 *   - **Progression** — poids, moyenne lissée, tendance, graphique et objectif
 *     (étape 8). Les statistiques et les activités Coros la rejoindront en V2.
 *   - **Réglages** — un sommaire et quatre sous-pages : thème, planning,
 *     notifications, compte. Le §6.8 des specs est couvert.
 *
 * Et depuis la V2 :
 *   - **Musculations** — deux sous-routes : les routines et le référentiel
 *     d'exercices, ce dernier ayant quitté la Bibliothèque après usage réel
 *     (28/08/2026). La séance en direct et l'historique la rejoindront à
 *     l'étape 6.
 *
 * 🔴 **Les sections sont de vraies routes, pas un état local.** Demandé après
 * usage : un appui sur un onglet doit ramener là où on l'avait laissé. Une
 * section rangée dans un `useState` disparaît au démontage de l'écran — donc
 * dès qu'on change d'onglet, c'est-à-dire exactement au moment où il faudrait
 * s'en souvenir. En route, elle est dans l'URL : la mémoire d'onglet
 * (`lib/tabMemory.ts`) n'a plus qu'à retenir un chemin, et le **retour par
 * glissement natif** traverse les sections sans qu'on ait rien à écrire.
 *
 * ⚠️ `/jour/:date` partage le composant de l'onglet Aujourd'hui : c'est la même
 * page, à une autre date. C'est `TabBar` qui se charge d'y garder
 * « Aujourd'hui » allumé — voir le commentaire de `alsoMatches`, `NavLink` seul
 * n'y arrive pas.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  Link,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigationType,
} from 'react-router';

import type { WorkoutSessionDTO } from '../../shared/types';
import { TabBar } from './components/ui/TabBar';
import { useOpenSession } from './hooks/useOpenSession';
import { remember, tabOf } from './lib/tabMemory';
import { AuthGate } from './screens/auth/AuthGate';
import { LibraryScreen } from './screens/library/LibraryScreen';
import { ProgressScreen } from './screens/progress/ProgressScreen';
import { MusculationScreen } from './screens/musculation/MusculationScreen';
import { SessionScreen } from './screens/musculation/SessionScreen';
import { SettingsScreen } from './screens/settings/SettingsScreen';
import { TodayScreen } from './screens/today/TodayScreen';

/**
 * Clé de transition : la **racine** de l'onglet, pas l'URL entière.
 *
 * 🔴 C'est ce qui empêche le fondu de se déclencher au changement de jour ou de
 * section. `/jour/2026-08-20` et `/jour/2026-08-21` partagent la même clé, tout
 * comme `/bibliotheque/aliments` et `/bibliotheque/recettes` : le carrousel du
 * pager garde son geste continu et les onglets de la bibliothèque ne clignotent
 * pas. Une clé complète aurait produit l'inverse exact de l'effet recherché.
 */
function tabKey(pathname: string): string {
  return tabOf(pathname);
}

/**
 * ⚠️ **La barre d'onglets ne défile jamais.** Le document est figé
 * (`overflow: hidden` sur `html`/`body`, cf. `index.html`) et c'est `<main>`
 * qui défile, sous une capsule d'onglets hors du flux. C'est ce qui donne le
 * comportement d'une application native attendu par les specs §8.1, plutôt
 * qu'une page web dont la barre s'en va avec le contenu.
 *
 * La géométrie est entièrement dans `styles/surfaces.css` : `.app-main` y réserve
 * la hauteur de la capsule, pour que le contenu passe **derrière** le verre
 * sans jamais rester coincé dessous.
 */
function Layout() {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  /** Défilement mémorisé par entrée d'historique — cf. l'effet ci-dessous. */
  const defilements = useRef(new Map<string, number>());

  /*
   * Alimente la mémoire d'onglet à chaque navigation.
   *
   * ⚠️ Dans un effet, et non pendant le rendu : écrire dans un module au milieu
   * d'un rendu est un effet de bord que React peut rejouer ou abandonner (mode
   * concurrent, `StrictMode` en développement). Ici la conséquence serait une
   * mémoire qui retient une page jamais affichée.
   */
  useEffect(() => {
    remember(pathname);
  }, [pathname]);

  /**
   * 🔴 **Une nouvelle page s'ouvre EN HAUT** *(31/08/2026, relevé sur
   * l'appareil : « quand je lance une séance, la page est déjà scrollée un
   * peu »)*.
   *
   * La `key` de `<main>` est l'**onglet**, pas le chemin — délibérément, c'est
   * elle qui joue le fondu au seul changement d'onglet. Conséquence : à
   * l'intérieur d'un onglet, `<main>` n'est jamais démonté et **garde son
   * défilement**. Partir d'une liste de routines défilée à 400 px pour ouvrir
   * une séance donnait donc une séance ouverte à 400 px.
   *
   * ⚠️ **On restaure au RETOUR, on remonte à l'aller** — et sans cette moitié
   * la correction serait une régression : remonter systématiquement ferait
   * perdre sa place dans la liste en revenant de la fiche qu'on vient d'y
   * ouvrir. C'est ce que fait une pile de navigation native, et `location.key`
   * donne exactement l'identité d'entrée d'historique qu'il faut pour ça.
   *
   * 🔴 **Le journal est exclu, et ce n'est pas un oubli.** Changer de jour est
   * une navigation (`/jour/:date`), mais le pager fait **partager** son
   * défilement d'une journée à l'autre : remonter en haut à chaque glissement
   * ferait sauter l'écran au milieu du geste, et l'on perdrait la comparaison
   * repas par repas qui est la raison même de glisser.
   *
   * ⚠️ **`useLayoutEffect` et non `useEffect`** : le positionnement doit tomber
   * avant la peinture, sinon on voit une image à l'ancien décalage.
   */
  useLayoutEffect(() => {
    const main = document.querySelector('.app-main');
    if (!(main instanceof HTMLElement)) return undefined;
    if (tabOf(pathname) === '') return undefined;

    /*
     * ⚠️ La `Map` est copiée dans une variable locale, comme
     * `react-hooks/exhaustive-deps` le demande : la règle ne peut pas savoir
     * que cette ref n'est jamais réassignée, et la faire taire coûterait plus
     * cher qu'une ligne.
     */
    const memoire = defilements.current;
    const memorise = memoire.get(key);
    main.scrollTop = navigationType === 'POP' && memorise !== undefined ? memorise : 0;

    /* Au départ de cette entrée, on retient où on en était. */
    return () => {
      memoire.set(key, main.scrollTop);
    };
  }, [key, pathname, navigationType]);

  /*
   * 🔴 **Le rappel est calculé ICI, pas dans le composant qui le rend** — parce
   * que la coque doit aussi **réserver sa place**. `.app-main` ne réserve que
   * la hauteur de la capsule d'onglets ; sans ce drapeau, la dernière carte de
   * chaque écran passerait sous la bannière. C'est le défaut que le commentaire
   * de `.app-main` s'engage précisément à ne jamais avoir : « rien n'est jamais
   * coincé sous la barre ».
   */
  const seanceOuverte = useOpenSession();
  const rappel =
    seanceOuverte !== null &&
    pathname !== `/musculation/seance/${encodeURIComponent(seanceOuverte.clientId)}`
      ? seanceOuverte
      : null;

  return (
    <div className="app-shell" data-nudge={rappel === null ? undefined : ''}>
      {/*
        ⚠️ **Un fondu, jamais un glissement horizontal** (cf. `surfaces.css`). Le
        §6.1 interdit d'intercepter le retour par glissement natif ; une
        transition qui déplacerait l'écran sur l'axe X entrerait en concurrence
        avec ce geste au moment précis où il se joue. Le fondu ne touche ni à la
        position, ni à `touch-action`, ni à l'historique.

        La `key` remonte le composant à chaque changement d'onglet — c'est ce
        qui relance l'animation, une classe seule ne se rejouant pas.
      */}
      <main className="app-main screen-enter" key={tabKey(pathname)}>
        <Outlet />
      </main>
      {rappel !== null && <SessionNudge session={rappel} />}
      <TabBar />
    </div>
  );
}

/**
 * 🔴 **Le rappel d'une séance en cours** *(31/08/2026, demandé après usage)* —
 * juste au-dessus de la barre d'onglets, et il y ramène d'un appui.
 *
 * Une séance dure 45 à 90 minutes et l'on en sort constamment : consulter un
 * aliment, corriger le journal, regarder une fiche. Sans rappel permanent, le
 * seul chemin de retour est de se souvenir qu'elle existe **et** de retrouver
 * l'onglet — c'est-à-dire deux choses de trop pendant qu'on a les mains prises.
 *
 * ⚠️ **Posé dans la coque, en un seul endroit**, comme la pastille de
 * synchronisation et le bouton de Réglages (V2 §3.5) : aucun écran ne peut
 * l'oublier, et aucun ne peut le placer autrement.
 *
 * ⚠️ **Masqué sur la séance elle-même** : un renvoi vers la page qu'on regarde
 * occupe la place sans rien offrir. C'est la règle déjà posée pour le bouton de
 * Réglages sur `/reglages`.
 *
 * ⚠️ **Et masqué sous une feuille ouverte**, par la même règle que la barre
 * d'onglets (`body[data-sheet-open]`) : il vit au même étage, il disparaît
 * avec elle.
 */
function SessionNudge({ session }: { session: WorkoutSessionDTO }) {
  const faites = session.sets.filter((set) => set.status === 'done').length;

  return (
    <Link
      className="app-session-nudge"
      to={`/musculation/seance/${encodeURIComponent(session.clientId)}`}
    >
      <span className="app-session-nudge__dot" aria-hidden />
      <span className="app-session-nudge__text">
        {/*
          ⚠️ **Le nom de la routine, pas « une séance »** : on peut en avoir
          oublié laquelle. Il retombe sur « Séance en cours » quand la routine a
          disparu — `routineName` est copié (§4.2), donc ce cas est rare, mais
          une séance ouverte hors routine n'en a jamais eu.
        */}
        <strong>{session.routineName ?? 'Séance en cours'}</strong>
        <span className="app-session-nudge__meta">
          {String(faites)} / {String(session.sets.length)} séries
        </span>
      </span>
      <span className="app-session-nudge__go" aria-hidden>
        Reprendre
      </span>
    </Link>
  );
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { index: true, element: <TodayScreen /> },
      // Même écran, à une autre date. La journée est créée paresseusement à
      // l'affichage (§4.5), y compris dans le passé.
      { path: 'jour/:date', element: <TodayScreen /> },

      /*
       * ⚠️ La section est un **paramètre facultatif** plutôt que quatre routes :
       * `/bibliotheque` doit rester une adresse valide — c'est la page par
       * défaut de l'onglet, celle où mène un second appui.
       */
      { path: 'bibliotheque', element: <LibraryScreen /> },
      { path: 'bibliotheque/:section', element: <LibraryScreen /> },

      /*
       * V2 étape 5 — le 5ᵉ onglet, à deux sous-onglets : les routines et le
       * référentiel d'exercices, ce dernier ayant quitté la Bibliothèque après
       * usage réel. La séance en direct et son historique sont le périmètre de
       * l'étape 6.
       */
      { path: 'musculation', element: <MusculationScreen /> },
      /*
       * ⚠️ **Déclarée avant `musculation/:section`** — et le classement de
       * react-router préfère de toute façon un segment littéral à un
       * paramètre, comme le routeur radix de Fastify. C'est écrit dans cet
       * ordre pour que le lecteur, lui, n'ait pas à le savoir : sans cela,
       * `/musculation/seance/xxx` *pourrait* se lire comme la section
       * « seance », qui n'existe pas et retomberait sur les routines.
       *
       * 🔴 **La séance est une route, pas un état de `MusculationScreen`.**
       * C'est la règle posée à l'étape 5 de la V1 (« sections et sous-pages
       * sont des ROUTES ») et elle paie ici plus qu'ailleurs : une séance dure
       * 45 à 90 minutes, on en sort pour consulter un exercice, et le **retour
       * par glissement natif** doit y ramener. En `useState`, elle
       * disparaîtrait au premier changement d'onglet — c'est-à-dire au pire
       * moment possible.
       */
      { path: 'musculation/seance/:clientId', element: <SessionScreen /> },
      /*
       * 🔴 **Le détail d'une routine est une route depuis le 31/08/2026**, et
       * c'était un défaut de fond : il vivait dans un `useState` de
       * `RoutinesSection`.
       *
       * Symptôme relevé à l'usage — « quand je suis dans une routine et que je
       * touche l'onglet Musculation, ça devrait revenir à la liste ». La barre
       * d'onglets faisait pourtant son travail : elle naviguait bien vers
       * `/musculation`. Seulement le composant n'était pas démonté pour
       * autant — même route, même position dans l'arbre — donc l'état local
       * survivait et l'écran ne bougeait pas.
       *
       * ⚠️ **C'est l'invariant de l'étape 5 de la V1, dans son symptôme
       * inverse.** Il était écrit qu'un `useState` « disparaît au changement
       * d'onglet, c'est-à-dire au moment précis où il faudrait s'en
       * souvenir » ; ici il **persiste** au moment précis où il faudrait
       * l'oublier. Les deux moitiés du même défaut, et la même correction.
       *
       * Ce que la route achète en plus, gratuitement : le **retour par
       * glissement natif** sort de la routine, et son adresse est partageable.
       */
      { path: 'musculation/routine/:clientId', element: <MusculationScreen /> },
      { path: 'musculation/:section', element: <MusculationScreen /> },

      /*
       * ⚠️ **Progression prend un sous-onglet en V2 étape 8** (specs §8.2 :
       * « + Statistiques, activités Coros »). Même forme que la Bibliothèque et
       * la Musculation — une section par segment d'URL, la racine servant la
       * section par défaut, une section inconnue y retombant.
       */
      { path: 'progression', element: <ProgressScreen /> },
      { path: 'progression/:section', element: <ProgressScreen /> },

      { path: 'reglages', element: <SettingsScreen /> },
      { path: 'reglages/:section', element: <SettingsScreen /> },
    ],
  },
]);

/**
 * La garde enveloppe le routeur, et non l'inverse : tant qu'il n'y a pas de
 * session, il n'y a ni onglets ni URL applicative — juste l'écran de connexion
 * ou celui de première configuration (§5.1, specs §9.2).
 */
export default function App() {
  return (
    <AuthGate>
      <RouterProvider router={router} />
    </AuthGate>
  );
}
